import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddPaymentMethod from './AddPaymentMethod';
import { apiGet } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './PaymentTypesList.css';

const PaymentMethodsList = ({ embedded = false }) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const [showAddEdit, setShowAddEdit] = useState(false);
    const [editMethod, setEditMethod] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            const response = await apiGet('/payments/methods');
            // Access response.data and ensure it's an array
            const data = response?.data || response || [];
            const paymentMethodsArray = Array.isArray(data) ? data : [];
            
            // Filter to show only active payment methods (case-insensitive)
            const activePaymentMethods = paymentMethodsArray.filter(method => 
                method.status && method.status.toLowerCase() === 'active'
            );
            
            setPaymentMethods(activePaymentMethods);
            setError('');
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            setError('Failed to load payment methods');
            toastError('Failed to load payment methods');
            setPaymentMethods([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditMethod(null);
        setShowAddEdit(true);
    };

    const handleEdit = (paymentMethod) => {
        setEditMethod(paymentMethod);
        setShowAddEdit(true);
    };

    const handleDelete = async (methodId, methodName) => {
        const confirmed = await toastConfirm(`Are you sure you want to delete the payment method "${methodName}"?`);
        if (!confirmed) return;

        try {
            setDeleting(true);
            const response = await apiClient.post(`/payments/methods/${methodId}/delete`);
            
            if (response.data.success) {
                toastSuccess('Payment method deleted successfully');
                fetchPaymentMethods(); // Refresh the list
            } else {
                toastError(response.data.message || 'Failed to delete payment method');
            }
        } catch (err) {
            console.error('Delete error:', err);
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to delete payment method';
            toastError(errorMessage);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
            sortable: true
        },
        {
            Header: 'Actions',
            accessor: 'id',
            disableSortBy: true,
            Cell: ({ row }) => {
                const paymentMethod = row.original || row.values || row;
                const methodId = paymentMethod.id;
                const methodName = paymentMethod.name;
                return (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <FaEdit 
                            style={{ cursor: 'pointer', color: '#153C7F', fontSize: '16px' }} 
                            onClick={() => handleEdit(paymentMethod)} 
                            title="Edit" 
                        />
                        <FaTrash 
                            style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '16px' }} 
                            onClick={() => handleDelete(methodId, methodName)} 
                            title="Delete" 
                        />
                    </div>
                );
            }
        }
    ];

    if (showAddEdit) {
        return <AddPaymentMethod method={editMethod} onClose={() => { setShowAddEdit(false); fetchPaymentMethods(); }} embedded={embedded} />;
    }

    if (loading) {
        if (embedded) {
            return <RTSpinner />;
        }
        return (
            <div className="rt-layout">
                <Menu className="rt-menu" activePath={location.pathname} />
                <div className="rt-main-content">
                    <RTSpinner />
                </div>
            </div>
        );
    }

    if (embedded) {
        return (
            <div style={{ background: 'transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <RTButton
                        variant="primary"
                        onClick={handleCreate}
                    >
                        Add Payment Method
                    </RTButton>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="table-container">
                    <Table
                        data={paymentMethods}
                        columns={columns}
                        exportable={false}
                        showSearch={false}
                        showTotalCount={true}
                        className="payment-types-table"
                    />
                </div>

                {/* Delete Loading Overlay */}
                {deleting && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}>
                        <RTSpinner size="medium" showMessage={true} message="Deleting payment method..." />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rt-layout">
            <Menu className="rt-menu" activePath={location.pathname} />
            <div className="rt-main-content">
                <div className="rt-title-row">
                    <h2 className="rt-title">Payment Methods</h2>
                    <div className="rt-actions">
                        <RTButton variant="secondary" onClick={() => navigate('/settings')}>
                            &#8592; Back
                        </RTButton>
                        <RTButton
                            variant="primary"
                            onClick={handleCreate}
                        >
                            Add Payment Method
                        </RTButton>
                    </div>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="rt-main-section">
                    {paymentMethods.length === 0 ? (
                        <div className="empty-state">
                            <p>No payment methods found.</p>
                            <RTButton variant="primary" onClick={handleCreate}>
                                Add First Payment Method
                            </RTButton>
                        </div>
                    ) : (
                        <Table
                            data={paymentMethods}
                            columns={columns}
                            loading={deleting}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodsList;
